import { MAP_IDS, MAP_NAMES, RANDOM_MAP, WAR_MAP_IDS, type AuthoredMapId, type MapId } from '@cratered/shared';
import catalogJson from './catalog.json';
import { LEVELS, type LevelDef } from './levels';
import { decodeSpec, familyName, generateLevel, type Family, type MapSpec } from './mapgen';
import { WAR_MAPS, isWarMap, warLevel, warMapDef, warTags } from './warmaps';

/**
 * Every playable map by id: the authored arenas, the catalogue of bot-tested generated maps
 * (catalog.json, written by scripts/mapgen-eval.ts) and inline `gen:` specs. Generated levels are
 * built once and cached; the same id always gives the same map on every client.
 */

export interface CatalogStats {
  /** Mean turns (full rounds) a fight lasted in bot testing. */
  rounds: number;
  /** Coefficient of variation of that length: lower is more repeatable. */
  cv: number;
  drawRate: number;
  /** |left win rate − 0.5|. */
  bias: number;
  matches: number;
  score: number;
}

export interface CatalogEntry {
  id: string;
  name: string;
  family: Family;
  spec: MapSpec;
  /** Set on the ten inspired variants of each winner. */
  parent?: string;
  blurb?: string;
  stats?: CatalogStats;
}

export interface Catalog {
  generated: string;
  maps: CatalogEntry[];
}

export const CATALOG: Catalog = catalogJson as unknown as Catalog;
const BY_ID = new Map(CATALOG.maps.map((m) => [m.id, m]));

/** Parents first, each followed by its variants: the lobby's grouped picker. */
export const CATALOG_GROUPS: { label: string; maps: { id: string; name: string }[] }[] = CATALOG.maps
  .filter((m) => !m.parent)
  .map((p) => ({
    label: `${p.name} (${familyName(p.family)})`,
    maps: [{ id: p.id, name: p.name }, ...CATALOG.maps.filter((m) => m.parent === p.id).map((m) => ({ id: m.id, name: m.name }))],
  }));

export function isAuthoredMap(id: MapId): id is AuthoredMapId {
  return (MAP_IDS as readonly string[]).includes(id);
}

/** Every map a match can be played on, in picker order: the arenas, the war maps, then the catalogue. */
export const PLAYABLE_MAP_IDS: readonly string[] = [...MAP_IDS, ...WAR_MAP_IDS, ...CATALOG.maps.map((m) => m.id)];

export interface MapCategory {
  id: 'raftwars' | 'war' | 'generated';
  label: string;
  blurb: string;
  maps: { id: string; name: string; tags: string[] }[];
}

/** The picker's categories: what a random pick can be narrowed to, with tags for finer filters. */
export const MAP_CATEGORIES: readonly MapCategory[] = [
  {
    id: 'raftwars', label: 'Raft Wars', blurb: 'Water arenas: rafts, boats, piers and islands.',
    maps: MAP_IDS.map((id) => ({ id, name: MAP_NAMES[id], tags: ['water', ...(LEVELS[id].catapults?.length ? ['catapults'] : [])] })),
  },
  {
    id: 'war', label: 'Territory War', blurb: 'Land arenas laid out after the Territory War stages.',
    maps: WAR_MAPS.map((m) => ({ id: m.id, name: m.name, tags: warTags(m) })),
  },
  {
    id: 'generated', label: 'Generated', blurb: 'Bot-tested maps from the generator, in families.',
    maps: CATALOG.maps.map((m) => ({ id: m.id, name: m.name, tags: ['generated', m.family] })),
  },
];

/** The ids a lobby pool should hold for a set of categories (and, when given, only these tags). */
export function poolFor(categories: readonly MapCategory['id'][], tags?: readonly string[]): string[] {
  return MAP_CATEGORIES.filter((c) => categories.includes(c.id))
    .flatMap((c) => c.maps.filter((m) => !tags || tags.length === 0 || m.tags.some((t) => tags.includes(t))).map((m) => m.id));
}

const cache = new Map<string, LevelDef>();

export function resolveLevel(id: MapId): LevelDef {
  if (isAuthoredMap(id)) return LEVELS[id];
  if (isWarMap(id)) return warLevel(id) ?? LEVELS[MAP_IDS[0]];
  const hit = cache.get(id);
  if (hit) return hit;
  let level: LevelDef | null = null;
  const entry = BY_ID.get(id);
  if (entry) level = generateLevel(entry.spec, { id, name: entry.name, blurb: entry.blurb });
  else {
    const spec = decodeSpec(id);
    if (spec) level = generateLevel(spec, { id, name: `${familyName(spec.family)} #${spec.seed % 1000}` });
  }
  if (!level) return LEVELS[MAP_IDS[0]];
  cache.set(id, level);
  return level;
}

export function mapName(id: MapId): string {
  if (id === RANDOM_MAP) return 'Random';
  if (isAuthoredMap(id)) return MAP_NAMES[id];
  const war = warMapDef(id);
  if (war) return war.name;
  return BY_ID.get(id)?.name ?? resolveLevel(id).name;
}

export function mapBlurb(id: MapId): string {
  if (id === RANDOM_MAP) return 'The server picks a map from the pool when the match starts.';
  return resolveLevel(id).blurb;
}

/** A catalogue map id other than `not`, or the first arena when the catalogue is empty. */
export function randomCatalogMap(not?: string, rng: () => number = Math.random): string {
  const pool = CATALOG.maps.filter((m) => m.id !== not);
  if (pool.length === 0) return MAP_IDS[0];
  return pool[Math.floor(rng() * pool.length)]!.id;
}
